import { useStore } from "zustand";
import { createStore } from "zustand/vanilla";

import { IUser } from "@interfaces/user.type";

interface IUserStore extends IUser {
  actions: {
    clearUser: () => void;
    setCurrentSearch: (searchId: string) => void;
    setUser: (payload: IUser) => void;
  };
}

/**
 * Create the Zustand store for managing the user state.
 */
const UserStore = createStore<IUserStore>()((set) => ({
  actions: {
    /**
     * Clears the user state, effectively logging the user out.
     */
    clearUser: () => {
      set({
        admin: false,
        authenticated: false,
        authToken: undefined,
        currentSearch: undefined,
        email: undefined,
        emailSignature: undefined,
        features: undefined,
        firstName: undefined,
        hasAcknowledgedProductUpdate: undefined,
        id: undefined,
        jobTitle: undefined,
        lastName: undefined,
        nylasAuthenticated: undefined,
        organizationId: undefined,
        organizationName: undefined,
        role: undefined,
        savedCampaignTemplate: undefined,
        sendEmailFrom: undefined,
      });
    },
    setCurrentSearch: (searchId) => {
      set({ currentSearch: searchId });
    },
    /**
     * Sets the user state with the provided payload.
     * @param payload - The user state data to set.
     */
    setUser: (payload) => {
      set({
        admin: payload.email
          ? payload.email.includes("licenselead.com") || payload.email.includes("scaleip.com")
          : false,
        authenticated: payload.authenticated ?? false,
        authToken: payload.authToken ?? null,
        currentSearch: payload.currentSearch ?? null,
        email: payload.email ?? null,
        emailSignature: payload.emailSignature ?? null,
        features: payload.features,
        firstName: payload.firstName ?? null,
        hasAcknowledgedProductUpdate: payload.hasAcknowledgedProductUpdate ?? null,
        id: payload.id,
        jobTitle: payload.jobTitle ?? null,
        lastName: payload.lastName ?? null,
        nylasAuthenticated: payload.nylasAuthenticated ?? null,
        nylasPermissions: payload.nylasPermissions ?? null,
        organizationId: payload.organizationId ? String(payload.organizationId) : null,
        organizationName: payload.organizationName ?? null,
        role: payload.role ?? null,
        savedCampaignTemplate: payload.savedCampaignTemplate ?? null,
        sendEmailFrom: payload.sendEmailFrom ?? null,
      });
    },
  },
  admin: false,
  authenticated: false,
  authToken: undefined,
  currentSearch: undefined,
  email: undefined,
  emailSignature: undefined,
  firstName: undefined,
  hasAcknowledgedProductUpdate: undefined,
  id: undefined,
  jobTitle: undefined,
  lastName: undefined,
  nylasAuthenticated: undefined,
  organizationId: undefined,
  organizationName: undefined,
  savedCampaignTemplate: undefined,
}));

/**
 * A custom hook to access the user state.
 */
function useUserStore(): IUserStore;
function useUserStore<Type>(selector: (state: IUserStore) => Type): Type;
function useUserStore<Type>(selector?: (state: IUserStore) => Type) {
  return useStore(UserStore, selector!);
}

const UserActions = UserStore.getState().actions;

const userState = () => UserStore.getState();

export { UserActions, userState, useUserStore };
